import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import MarketingLayout from "./MarketingLayout";
import { POSTS, getPostBySlug } from "@/lib/blogPosts";
import { useSeo } from "@/lib/seo";
import { ArrowLeft } from "lucide-react";

const fmtDate = (iso) => {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  // Compute related posts BEFORE useSeo so they can be plumbed into
  // the BlogPosting schema as `relatedLink`. Same logic as the
  // visible "Keep reading" section below — single source of truth.
  const relatedComputed = post
    ? (() => {
        const explicit = (post.relatedSlugs || [])
          .map((s) => POSTS.find((p) => p.slug === s))
          .filter(Boolean);
        return explicit.length > 0
          ? explicit.slice(0, 3)
          : POSTS.filter((p) => p.slug !== post.slug).slice(0, 2);
      })()
    : [];

  useSeo({
    title: post ? `${post.title} – FamFeel Blog` : "Blog – FamFeel",
    description: post?.excerpt || "Essay from the FamFeel Blog.",
    path: post ? `/blog/${post.slug}` : "/blog",
    jsonLdId: "ld-post",
    jsonLd: post
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "BlogPosting",
              "headline": post.title,
              "description": post.excerpt,
              "datePublished": post.date,
              "dateModified": post.date,
              "mainEntityOfPage": {
                "@type": "WebPage",
                "@id": `${origin}/blog/${post.slug}`,
              },
              "author": { "@type": "Organization", "name": "FamFeel" },
              "publisher": {
                "@type": "Organization",
                "name": "FamFeel",
                "logo": {
                  "@type": "ImageObject",
                  "url": `${origin}/icon-512.png`,
                },
              },
              "image": `${origin}/og-image.png`,
              "url": `${origin}/blog/${post.slug}`,
              // `relatedLink` is the schema.org-blessed signal for
              // "these other URLs share a topic with this article".
              // Google reads it for sitelinks + topical clustering;
              // Bing uses it directly for "related articles" carousels.
              ...(relatedComputed.length > 0 && {
                relatedLink: relatedComputed.map(
                  (p) => `${origin}/blog/${p.slug}`
                ),
              }),
              // Topical keywords — comma-separated string per
              // schema.org spec. Helps Google's NLP cluster this
              // article with its peers without forcing us to maintain
              // a separate tag taxonomy.
              ...(post.keywords && post.keywords.length > 0 && {
                keywords: post.keywords.join(", "),
              }),
              // `isPartOf` ties every post into a single Blog graph
              // node so search engines understand /blog/* as one
              // content cluster, not isolated pages.
              "isPartOf": {
                "@type": "Blog",
                "@id": `${origin}/blog`,
                "name": "FamFeel Blog",
                "url": `${origin}/blog`,
              },
            },
            {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": `${origin}/` },
                { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${origin}/blog` },
                { "@type": "ListItem", "position": 3, "name": post.title, "item": `${origin}/blog/${post.slug}` },
              ],
            },
          ],
        }
      : null,
  });

  if (!post) return <Navigate to="/blog" replace />;

  const related = relatedComputed;

  return (
    <MarketingLayout>
      <article className="px-6 md:px-10 pt-8 md:pt-14 pb-16 max-w-3xl">
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 label-tiny text-[color:var(--ink-3)] hover:text-[color:var(--ink)] transition-colors mb-8"
          data-testid="blog-back"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.75} />
          The Blog
        </Link>

        <div
          className="w-20 h-20 md:w-24 md:h-24 rounded-xl flex items-center justify-center text-4xl md:text-5xl mb-6"
          style={{ background: post.tint }}
          aria-hidden="true"
        >
          {post.heroEmoji}
        </div>

        <div className="label-tiny text-[color:var(--ink-3)] mb-4">
          {fmtDate(post.date)} &nbsp;·&nbsp; {post.readMinutes} min read
        </div>
        <h1
          className="font-display-italic text-4xl md:text-6xl leading-[1.02] tracking-tight mb-8"
          data-testid="post-h1"
        >
          {post.title}
        </h1>

        <div className="space-y-5">
          {post.body.map((block, i) => {
            if (block.type === "h") {
              return (
                <h2
                  key={i}
                  className="font-display text-2xl md:text-3xl leading-tight tracking-tight pt-4"
                >
                  {block.text}
                </h2>
              );
            }
            if (block.type === "table") {
              // Real <table> markup so search engines parse the
              // comparison natively (Google reads HTML tables for
              // "vs" rich snippets — no separate schema required,
              // just proper <thead> / <tbody> / <th scope=...> hints).
              const headers = block.headers || [];
              const rows = block.rows || [];
              return (
                <div
                  key={i}
                  className="overflow-x-auto -mx-1 my-2"
                  data-testid={`blog-table-${i}`}
                >
                  {block.caption && (
                    <div className="label-tiny text-[color:var(--ink-3)] mb-2 italic">
                      {block.caption}
                    </div>
                  )}
                  <table className="w-full text-[14px] md:text-[15px] border-collapse">
                    <thead>
                      <tr className="border-b border-[color:var(--line)]">
                        {headers.map((h, hi) => (
                          <th
                            key={hi}
                            scope="col"
                            className={`text-left py-2.5 px-2 font-display tracking-tight ${
                              hi === 0
                                ? "text-[color:var(--ink-3)] font-normal"
                                : "text-[color:var(--ink)]"
                            }`}
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r, ri) => (
                        <tr
                          key={ri}
                          className="border-b border-[color:var(--line-soft)] last:border-b-0"
                        >
                          {r.map((cell, ci) => (
                            <td
                              key={ci}
                              className={`py-2.5 px-2 align-top ${
                                ci === 0
                                  ? "text-[color:var(--ink-2)] font-display-italic"
                                  : "text-[color:var(--ink)]"
                              }`}
                            >
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            }
            return (
              <p
                key={i}
                className="text-[17px] md:text-[18px] leading-[1.55] text-[color:var(--ink-2)]"
              >
                {block.text}
              </p>
            );
          })}
        </div>

        <div className="mt-14 pt-10 border-t border-[color:var(--line-soft)]">
          <div className="label-tiny mb-4">Keep reading</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="rounded-xl border border-[color:var(--line)] p-5 hover:bg-[color:var(--bg-soft)] transition-colors"
                data-testid={`blog-related-${p.slug}`}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                    style={{ background: p.tint }}
                    aria-hidden="true"
                  >
                    {p.heroEmoji}
                  </div>
                  <div className="min-w-0">
                    <div className="font-display text-lg leading-tight tracking-tight">
                      {p.title}
                    </div>
                    <div className="label-tiny text-[color:var(--ink-3)] mt-1">
                      {p.readMinutes} min read
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>

        <div className="mt-14 text-center">
          <Link
            to="/app?mode=create"
            className="ff-btn ff-btn-primary"
            data-testid="post-cta"
          >
            Start your family →
          </Link>
          <div className="label-tiny text-[color:var(--ink-3)] mt-3">
            Free for 4 &nbsp;·&nbsp; No ads &nbsp;·&nbsp; Private by default
          </div>
        </div>
      </article>
    </MarketingLayout>
  );
}
